import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Video,
  FileText,
  Activity,
  ArrowUpRight,
  CheckCircle2,
  Clock,
  Plus,
  List,
  Trash2,
  ExternalLink,
  Pencil,
  Calendar,
  Newspaper,
  CalendarPlus,
  FilePlus2,
} from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useCurrentUser } from '@/hooks/redux';
import { useGetSessionsQuery } from '@/store/api/sessionsApi';
import { useGetEventsQuery } from '@/store/api/eventsApi';
import { useGetNewsQuery } from '@/store/api/newsApi';
import { formatDistanceToNow } from 'date-fns';

interface MetricCardProps {
  title: string;
  value: string;
  icon: React.ComponentType<{ className?: string }>;
}

function MetricCard({ title, value, icon: Icon }: MetricCardProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
      </CardContent>
    </Card>
  );
}

export function DashboardPage() {
  const { t } = useTranslation();
  const user = useCurrentUser();

  // Fetch metrics concurrently
  const { data: totalSessionsData, isLoading: isLoadingTotal } = useGetSessionsQuery({ take: 1 });
  const { data: publishedSessionsData, isLoading: isLoadingPublished } = useGetSessionsQuery({
    take: 1,
    status: 'Published',
  });
  const { data: draftSessionsData, isLoading: isLoadingDraft } = useGetSessionsQuery({
    take: 1,
    status: 'Draft',
  });
  const { data: totalEventsData, isLoading: isLoadingEvents } = useGetEventsQuery({ take: 1 });
  const { data: totalNewsData, isLoading: isLoadingNews } = useGetNewsQuery({ take: 1 });

  const { data: recentSessionsData, isLoading: isLoadingRecentSessions } = useGetSessionsQuery({
    take: 5,
  });
  const { data: recentEventsData, isLoading: isLoadingRecentEvents } = useGetEventsQuery({
    take: 5,
  });
  const { data: recentNewsData, isLoading: isLoadingRecentNews } = useGetNewsQuery({ take: 5 });

  const metrics = [
    {
      title: 'Total Sessions',
      value: isLoadingTotal ? '...' : String(totalSessionsData?.count || 0),
      icon: Video,
    },
    {
      title: 'Published Sessions',
      value: isLoadingPublished ? '...' : String(publishedSessionsData?.count || 0),
      icon: CheckCircle2,
    },
    {
      title: 'Draft Sessions',
      value: isLoadingDraft ? '...' : String(draftSessionsData?.count || 0),
      icon: FileText,
    },
    {
      title: 'Total Events',
      value: isLoadingEvents ? '...' : String(totalEventsData?.count || 0),
      icon: Calendar,
    },
    {
      title: 'Total News',
      value: isLoadingNews ? '...' : String(totalNewsData?.count || 0),
      icon: Newspaper,
    },
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-3xl font-bold tracking-tight">{t('dashboard.welcome')}</h2>
        <p className="text-muted-foreground">
          {user?.name && `Welcome back, ${user.name}! `}
          Here is the overview of your content and sessions.
        </p>
      </div>

      {/* Metrics Grid */}
      <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-5">
        {metrics.map((metric, index) => (
          <MetricCard key={index} {...metric} />
        ))}
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-7">
        <div className="col-span-4 lg:col-span-5">
          <Tabs defaultValue="sessions" className="w-full">
            <div className="flex items-center justify-between mb-4">
              <TabsList>
                <TabsTrigger value="sessions">Sessions</TabsTrigger>
                <TabsTrigger value="events">Events</TabsTrigger>
                <TabsTrigger value="news">News</TabsTrigger>
              </TabsList>
            </div>

            {/* Sessions Tab */}
            <TabsContent value="sessions" className="m-0 mt-2">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Recently Edited Sessions</CardTitle>
                      <CardDescription>Your most recently modified content</CardDescription>
                    </div>
                    <Button variant="ghost" size="sm" asChild>
                      <Link to="/sessions">View all</Link>
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {isLoadingRecentSessions ? (
                      <div className="py-4 text-center text-sm text-muted-foreground">
                        Loading recent sessions...
                      </div>
                    ) : recentSessionsData?.data?.length === 0 ? (
                      <div className="py-4 text-center text-sm text-muted-foreground">
                        No sessions created yet.
                      </div>
                    ) : (
                      recentSessionsData?.data?.map((session) => (
                        <div
                          key={session.id}
                          className="flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
                        >
                          <div className="flex items-center space-x-4">
                            <div className="p-2 bg-background border rounded-md">
                              <Video className="h-4 w-4 text-primary" />
                            </div>
                            <div>
                              <Link
                                to={`/sessions/${session.id}/edit`}
                                className="font-medium text-sm hover:underline"
                              >
                                {session.title}
                              </Link>
                              <div className="flex items-center text-xs text-muted-foreground mt-1">
                                <span className="truncate max-w-[200px]">{session.speaker}</span>
                                <span className="mx-2">•</span>
                                <Clock className="mr-1 h-3 w-3" />
                                <span>
                                  {session.updatedAt
                                    ? formatDistanceToNow(new Date(session.updatedAt), {
                                        addSuffix: true,
                                      })
                                    : 'Recently'}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge
                              variant={session.status === 'Published' ? 'default' : 'warning'}
                              className="text-[10px]"
                            >
                              {session.status}
                            </Badge>
                            <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
                              <Link to={`/sessions/${session.id}/edit`}>
                                <Pencil className="h-4 w-4" />
                              </Link>
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Events Tab */}
            <TabsContent value="events" className="m-0 mt-2">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Recently Edited Events</CardTitle>
                      <CardDescription>Your most recently modified events</CardDescription>
                    </div>
                    <Button variant="ghost" size="sm" asChild>
                      <Link to="/events">View all</Link>
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {isLoadingRecentEvents ? (
                      <div className="py-4 text-center text-sm text-muted-foreground">
                        Loading recent events...
                      </div>
                    ) : recentEventsData?.data?.length === 0 ? (
                      <div className="py-4 text-center text-sm text-muted-foreground">
                        No events created yet.
                      </div>
                    ) : (
                      recentEventsData?.data?.map((event) => (
                        <div
                          key={event.id}
                          className="flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
                        >
                          <div className="flex items-center space-x-4">
                            <div className="p-2 bg-background border rounded-md">
                              <Calendar className="h-4 w-4 text-primary" />
                            </div>
                            <div>
                              <Link
                                to={`/events/${event.id}/edit`}
                                className="font-medium text-sm hover:underline"
                              >
                                {event.title}
                              </Link>
                              <div className="flex items-center text-xs text-muted-foreground mt-1">
                                <Clock className="mr-1 h-3 w-3" />
                                <span>
                                  {event.updatedAt
                                    ? formatDistanceToNow(new Date(event.updatedAt), {
                                        addSuffix: true,
                                      })
                                    : 'Recently'}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge
                              variant={event.status === 'Published' ? 'default' : 'warning'}
                              className="text-[10px]"
                            >
                              {event.status}
                            </Badge>
                            <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
                              <Link to={`/events/${event.id}/edit`}>
                                <Pencil className="h-4 w-4" />
                              </Link>
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* News Tab */}
            <TabsContent value="news" className="m-0 mt-2">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Recently Edited News</CardTitle>
                      <CardDescription>Your most recently modified news articles</CardDescription>
                    </div>
                    <Button variant="ghost" size="sm" asChild>
                      <Link to="/news">View all</Link>
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {isLoadingRecentNews ? (
                      <div className="py-4 text-center text-sm text-muted-foreground">
                        Loading recent news...
                      </div>
                    ) : recentNewsData?.data?.length === 0 ? (
                      <div className="py-4 text-center text-sm text-muted-foreground">
                        No news created yet.
                      </div>
                    ) : (
                      recentNewsData?.data?.map((news) => (
                        <div
                          key={news.id}
                          className="flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
                        >
                          <div className="flex items-center space-x-4">
                            <div className="p-2 bg-background border rounded-md">
                              <Newspaper className="h-4 w-4 text-primary" />
                            </div>
                            <div>
                              <Link
                                to={`/news/${news.id}/edit`}
                                className="font-medium text-sm hover:underline"
                              >
                                {news.title}
                              </Link>
                              <div className="flex items-center text-xs text-muted-foreground mt-1">
                                <Clock className="mr-1 h-3 w-3" />
                                <span>
                                  {news.updatedAt
                                    ? formatDistanceToNow(new Date(news.updatedAt), {
                                        addSuffix: true,
                                      })
                                    : 'Recently'}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge
                              variant={news.status === 'Published' ? 'default' : 'warning'}
                              className="text-[10px]"
                            >
                              {news.status}
                            </Badge>
                            <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
                              <Link to={`/news/${news.id}/edit`}>
                                <Pencil className="h-4 w-4" />
                              </Link>
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Quick Actions */}
        <Card className="col-span-3 lg:col-span-2">
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
            <CardDescription>Common tasks and shortcuts</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3">
              <Button variant="outline" className="h-14 justify-start" asChild>
                <Link to="/sessions/create">
                  <Plus className="mr-3 h-5 w-5 text-muted-foreground" />
                  <div className="flex flex-col items-start">
                    <span className="font-medium">Create Session</span>
                    <span className="text-xs text-muted-foreground">
                      Draft a new talk or workshop
                    </span>
                  </div>
                </Link>
              </Button>
              <Button variant="outline" className="h-14 justify-start" asChild>
                <Link to="/events">
                  <CalendarPlus className="mr-3 h-5 w-5 text-muted-foreground" />
                  <div className="flex flex-col items-start">
                    <span className="font-medium">Create Event</span>
                    <span className="text-xs text-muted-foreground">Schedule a new event</span>
                  </div>
                </Link>
              </Button>
              <Button variant="outline" className="h-14 justify-start" asChild>
                <Link to="/news">
                  <FilePlus2 className="mr-3 h-5 w-5 text-muted-foreground" />
                  <div className="flex flex-col items-start">
                    <span className="font-medium">Create News</span>
                    <span className="text-xs text-muted-foreground">Publish a new article</span>
                  </div>
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
